// Shared formatting utilities used across all dashboard pages

import { formatPlainDate } from '@/lib/calendarMonths';

// Format phone number as (XXX) XXX-XXXX
export const formatPhoneNumber = (value) => {
  if (!value) return '';
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Parse phone number to digits only for storage.
// Capped at ten digits because formatPhoneNumber only ever renders ten: without
// the cap an eleventh keystroke left the field reading "(512) 555-1234" while an
// 11-digit value went to the server, so what you saw was not what you saved.
export const parsePhoneNumber = (value) => {
  if (!value) return '';
  return value.replace(/[^\d]/g, '').slice(0, 10);
};

// ZIP entry: digits only, hyphen supplied by us for ZIP+4.
//
// The applicant types digits and nothing else; the hyphen is inserted rather
// than typed. Output is the value we store, so it has to satisfy the
// ^\d{5}(-\d{4})?$ rule in applicationValidation — which is why nine digits
// come back hyphenated instead of bare.
export const formatZipCode = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

// Format currency with commas (no $ prefix)
export const formatCurrency = (value) => {
  if (!value && value !== 0) return '';
  const num = typeof value === 'string' ? value.replace(/[^\d.]/g, '') : value;
  const parts = num.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

// Format currency with $ prefix
export const formatCurrencyDisplay = (value) => {
  if (!value && value !== 0) return '';
  const num = typeof value === 'string' ? value.replace(/[^\d.]/g, '') : value;
  const parts = num.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return '$' + parts.join('.');
};

// Parse currency to number for storage
export const parseCurrency = (value) => {
  if (!value) return '';
  return value.replace(/[^\d.]/g, '');
};

// US States list
export const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }, { value: 'DC', label: 'District of Columbia' }
];

// Case Types
export const CASE_TYPES = [
  { value: 'auto_accident', label: 'Auto Accident/MVA' },
  { value: 'rideshare_accident', label: 'Ride Share Auto Accident' },
  { value: 'slip_fall', label: "Slip and Fall / Injury on Other's Property" },
  { value: 'dog_bite', label: 'Dog Bite' },
  { value: 'medical_malpractice', label: 'Medical Malpractice' },
  { value: 'other', label: 'Other' }
];

// Insurance Carriers
export const INSURANCE_CARRIERS = [
  { value: 'state_farm', label: 'State Farm' },
  { value: 'geico', label: 'GEICO' },
  { value: 'progressive', label: 'Progressive' },
  { value: 'allstate', label: 'Allstate' },
  { value: 'liberty_mutual', label: 'Liberty Mutual' },
  { value: 'nationwide', label: 'Nationwide' },
  { value: 'travelers', label: 'Travelers' },
  { value: 'usaa', label: 'USAA' },
  { value: 'farmers', label: 'Farmers' },
  { value: 'other', label: 'Other' }
];

// Liability Policy Limits
export const LIABILITY_LIMITS = [
  { value: '25/50', label: '25/50' },
  { value: '50/100', label: '50/100' },
  { value: '100/300', label: '100/300' },
  { value: '250/500', label: '250/500' },
  { value: '500/500', label: '500/500' },
  { value: '1M/1M', label: '1M/1M' },
  { value: 'unknown', label: 'Unknown' }
];

// Treatment locations (used on application Section 5)
export const TREATMENT_LOCATIONS = [
  { value: 'hospital', label: 'Hospital' },
  { value: 'urgent_care', label: 'Urgent Care' },
  { value: 'emergency_room', label: 'Emergency Room' },
  { value: 'pcp', label: 'PCP - Primary Care Physician' },
  { value: 'specialist', label: 'Specialist' },
];

export const formatTreatmentLocations = (values) => {
  if (!values || !Array.isArray(values) || values.length === 0) return '';
  return values.map(v => TREATMENT_LOCATIONS.find(o => o.value === v)?.label || v).join(', ');
};

export const TREATMENTS_COMPLETED_OPTIONS = [
  { value: '1-5', label: '1-5' },
  { value: '6-10', label: '6-10' },
  { value: '11-20', label: '11-20' },
  { value: '20+', label: '20+' },
];

// Get insurance carrier label from value
export const getInsuranceCarrierLabel = (value) => {
  const found = INSURANCE_CARRIERS.find(c => c.value === value);
  return found ? found.label : value || 'Not specified';
};

// Get liability limits label from value
export const getLiabilityLimitsLabel = (value) => {
  const found = LIABILITY_LIMITS.find(l => l.value === value);
  return found ? found.label : value || 'Not specified';
};

// Status display mapping
export const STATUS_CONFIG = {
  draft: { label: 'Draft', variant: 'secondary' },
  submitted: { label: 'Submitted', variant: 'info' },
  pending_lawyer_review: { label: 'Pending Lawyer Review', variant: 'warning' },
  pending_client_action: { label: 'Pending Client Action', variant: 'warning' },
  available_to_funders: { label: 'Available to Funders', variant: 'primary' },
  under_review: { label: 'Under Review', variant: 'info' },
  offer_sent: { label: 'Offer Sent', variant: 'purple' },
  approved: { label: 'Lawyer Approved', variant: 'success' },
  pending_offers: { label: 'Pending Offer(s)', variant: 'primary' },
  declined: { label: 'Declined', variant: 'danger' },
  funded: { label: 'Funded', variant: 'success' },
  closed: { label: 'Closed', variant: 'secondary' },
  pending: { label: 'Pending', variant: 'warning' },
  accepted: { label: 'Accepted', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'danger' },
  canceled: { label: 'Canceled', variant: 'secondary' },
  cancelled: { label: 'Canceled', variant: 'secondary' },
  withdrawn: { label: 'Withdrawn', variant: 'secondary' },
};

// --- Dates and times ------------------------------------------------------
//
// Every timestamp this app displays is rendered in Eastern Time, for everyone,
// regardless of where the viewer's browser is. The business runs on Eastern;
// an admin in Denver reading "8 hrs ago" next to a lawyer in Atlanta reading
// something else made the engagement columns unusable for comparison.
//
// Getting that right needs BOTH halves below. They are separate bugs and
// fixing only the second leaves the display wrong:
//
//   1. Parse the right instant. The API serialises Mongo datetimes with NO
//      timezone marker — "2026-08-14T04:23:15.123000" — because the motor
//      client is not tz_aware (see backend/database.py). Per spec, JS parses
//      an offset-less date-TIME as *local*, so `new Date(...)` on an Eastern
//      browser built an instant four hours off and the last-login column read
//      "8 hrs ago" for a login that happened 12 hours earlier. A few fields
//      are stored as `.isoformat()` strings instead and DO carry "+00:00", so
//      the parser has to handle both shapes.
//   2. Render in the right zone. `toLocaleString()` with no `timeZone` uses
//      the viewer's zone, which is only Eastern by luck.
//
// Use parseApiDate for anything that came from the API. Never `new Date()` on
// an API timestamp directly.

export const APP_TIME_ZONE = 'America/New_York';
export const APP_TIME_ZONE_LABEL = 'ET';

// "2026-08-14T04:23:15.123456" / "2026-08-14 04:23:15" — a wall clock with no
// zone attached. Everything the backend reads out of Mongo looks like this.
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/;
// "2026-08-14" — a calendar date with no time at all (dateOfBirth, incidentDate).
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Turn an API value into the instant it actually names, or null.
 *
 * Naive datetimes are read as UTC, which is what the backend meant by them.
 * Date-only values are anchored at noon UTC so that rendering them in Eastern
 * (or any zone within ~11 hours of UTC) lands on the date as written — the
 * off-by-one day that `new Date('2026-08-14')` produces west of UTC.
 */
export const parseApiDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const fromEpoch = new Date(value);
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }
  if (typeof value !== 'string') return null;

  const raw = value.trim();
  let normalized = raw;
  if (DATE_ONLY.test(raw)) {
    normalized = `${raw}T12:00:00Z`;
  } else if (NAIVE_DATE_TIME.test(raw)) {
    normalized = `${raw.replace(' ', 'T')}Z`;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Render an API timestamp in Eastern Time with the given Intl options.
 * Returns '' when the value is empty and the original string when it can't be
 * parsed, so a bad value shows as itself rather than "Invalid Date".
 */
export const formatInAppZone = (value, options) => {
  if (value === null || value === undefined || value === '') return '';
  const date = parseApiDate(value);
  if (!date) return typeof value === 'string' ? value : '';
  return date.toLocaleString('en-US', { ...options, timeZone: APP_TIME_ZONE });
};

// Format an absolute datetime (ISO string or Date) for display as e.g. "Nov 5, 2028"
export const formatDate = (dateString) =>
  formatInAppZone(dateString, { year: 'numeric', month: 'short', day: 'numeric' });

// Format a YYYY-MM-DD date string (with no time/zone component) for display
// as e.g. "Nov 5, 2028" — without the timezone-shift bug `new Date('2028-11-05')`
// would cause in browsers west of UTC. parseApiDate anchors these at noon UTC,
// so this is now just formatDate; both are kept because the call sites carry
// real meaning: formatDateOnly marks a field that has no time to lose.
export const formatDateOnly = (dateString) => formatDate(dateString);

// Format an instant with its time of day, e.g. "Nov 5, 2028, 12:23 AM ET".
// The zone label is not decoration — without it a viewer in another zone reads
// the time as their own.
export const formatDateTime = (dateString) => {
  const formatted = formatInAppZone(dateString, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
  if (!formatted || formatted === dateString) return formatted;
  return `${formatted} ${APP_TIME_ZONE_LABEL}`;
};

// Today's calendar date in Eastern, as YYYY-MM-DD for <input type="date">.
// `new Date().toISOString().split('T')[0]` was returning tomorrow's date to
// anyone using the app after 8pm Eastern.
export const todayInAppZone = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: APP_TIME_ZONE,
  }).format(new Date());
  return parts;
};

// Get case type label from value
export const getCaseTypeLabel = (value) => {
  const found = CASE_TYPES.find(ct => ct.value === value);
  return found ? found.label : value || 'Not specified';
};


// Render an ISO timestamp as a human-friendly "x time ago" string,
// used in admin tables (e.g. last-login engagement column).
//
// This is zone-independent — an elapsed duration is the same everywhere — but
// it still depends on parsing the right instant. Through plain `new Date()` an
// offset-less API timestamp was read as local, so on an Eastern browser every
// one of these was four hours short: a 12-hour-old login read "8 hrs ago".
export const formatRelativeTime = (dateString) => {
  const d = parseApiDate(dateString);
  if (!d) return 'Never';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'Just now';
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    return `${m} min${m === 1 ? '' : 's'} ago`;
  }
  if (secs < 86400) {
    const h = Math.floor(secs / 3600);
    return `${h} hr${h === 1 ? '' : 's'} ago`;
  }
  if (secs < 86400 * 30) {
    const days = Math.floor(secs / 86400);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  if (secs < 86400 * 365) {
    const months = Math.floor(secs / (86400 * 30));
    return `${months} mo${months === 1 ? '' : 's'} ago`;
  }
  const years = Math.floor(secs / (86400 * 365));
  return `${years} yr${years === 1 ? '' : 's'} ago`;
};

// A repayment band's period, e.g. "0–6 months" — the window a band covers,
// not a single date. Extracted from FunderOfferBuilder's renderBandRow:
// CompareOffers (Task 10) needs the identical label for the same kind of
// row, so this moved out on the second use rather than being copied.
export const formatBandPeriodLabel = (fromMonth, toMonth) => `${fromMonth}–${toMonth} months`;

// A repayment band's date range, e.g. "09/09/2026 – 03/08/2027". The
// terminal band's `to` is null and reads as open-ended ("any date after")
// rather than as a blank or a repeated date — settling never actually stops
// being possible, the schedule just has nothing further to say about cost.
export const formatBandDateRangeLabel = (from, to) =>
  `${formatPlainDate(from)} – ${to ? formatPlainDate(to) : 'any date after'}`;
